import { Controller } from '@hotwired/stimulus'

// Restores a horizontal tab strip's scroll position on load (per browser tab, via
// sessionStorage) so navigating between link-based tabs keeps the strip where it was
// instead of jumping. If the restored position wouldn't show the active tab — a new
// browser tab, a deep tab opened directly, or an active tab changed without scrolling
// to it — it nudges just far enough to bring the active tab into view. Only ever
// scrolls the strip, never the page.
//
// The key must be stable across renders — it is what matches a saved position to the strip on
// the next load, so a generated value silently disables persistence. Omit it entirely and
// persistence is skipped (nothing read or written); the active tab is still kept in view.
//
//   data: { controller: 'tab-scroll', 'tab-scroll-key-value': 'my-stable-id' }
export default class extends Controller {
  static values = { key: String }

  connect() {
    // The strip is often inserted before scroll layout is committed (Turbo visits, lazily
    // loaded frames), where scrollWidth and clientWidth are still 0 and any scroll clamps to
    // 0. Waiting a frame gets real geometry, and for those insertions rAF still runs before
    // the frame is painted. On a cold load it cannot: the JS packs are deferred, so this
    // controller first connects well after the browser has painted the strip at 0.
    this.pendingScroll = requestAnimationFrame(() => {
      this.pendingScroll = null
      this.applyScroll()
    })
    this.element.addEventListener('scroll', this.save, { passive: true })
  }

  disconnect() {
    this.element.removeEventListener('scroll', this.save)
    if (this.pendingScroll) cancelAnimationFrame(this.pendingScroll)
    if (this.pendingSave) cancelAnimationFrame(this.pendingSave)
  }

  // Persist the position, throttled to one write per frame. No disconnect-time save:
  // during a Turbo visit scrollLeft is already 0 by then, which would clobber the value.
  save = () => {
    if (this.pendingSave) return
    this.pendingSave = requestAnimationFrame(() => {
      this.pendingSave = null
      if (!this.hasKeyValue) return
      try {
        window.sessionStorage.setItem(this.storageKey, String(this.element.scrollLeft))
      } catch {
        // sessionStorage may be unavailable (private mode, quota); persistence is best-effort.
      }
    })
  }

  applyScroll() {
    // `behavior: 'instant'` overrides the strip's `scroll-behavior: smooth` so it lands
    // before the first paint instead of animating in from the start.
    this.element.scrollTo({ left: this.targetScrollLeft(), behavior: 'instant' })
  }

  // The saved scroll position, nudged just enough to keep the active tab visible.
  targetScrollLeft() {
    const strip = this.element
    const saved = this.savedScrollLeft()
    let target = saved === null ? strip.scrollLeft : saved

    const active = strip.querySelector('.tab-list__tab--active')
    if (active) {
      const stripRect = strip.getBoundingClientRect()
      const activeRect = active.getBoundingClientRect()
      // Active tab position in content coordinates (independent of the current scroll).
      const activeLeft = strip.scrollLeft + (activeRect.left - stripRect.left)
      const activeRight = activeLeft + activeRect.width
      const viewport = strip.clientWidth

      if (activeRight > target + viewport) target = activeRight - viewport
      else if (activeLeft < target) target = activeLeft
    }

    return Math.max(0, Math.min(target, strip.scrollWidth - strip.clientWidth))
  }

  savedScrollLeft() {
    if (!this.hasKeyValue) return null

    try {
      const saved = window.sessionStorage.getItem(this.storageKey)
      return saved === null ? null : parseInt(saved, 10) || 0
    } catch {
      return null
    }
  }

  get storageKey() {
    return `tab-scroll:${this.keyValue}`
  }
}
